import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { CSSTransition, SwitchTransition } from "react-transition-group";

// Tab Components
import { IntroductionTab } from "./tabs/IntroductionTab";
import { ItineraryTab } from "./tabs/ItineraryTab";
import { IncludesTab } from "./tabs/IncludesTab";
import { TripBookTab } from "./tabs/TripBookTab";
import { NotesTab } from "./tabs/NotesTab";
import {TourCatalog, TourOperator, TripItinerary} from "./contentfulModel.ts";
import {HotelsTab} from "./tabs/HotelsTab.tsx";
import {OthersTab} from "./tabs/OthersTab.tsx";
import {isMiceCatalog} from "./utils.tsx";

interface TourCatalogContentProps {
    tourOperator: TourOperator,
    tourPackage: TripItinerary;
    tourCatalog: TourCatalog;
    goToSlide: (slideIndex: number) => void;
}

type TabType = 'introduction' | 'itinerary' | 'includes' | 'tripbook' | 'notes' | 'hotels';

export const TourCatalogContent: React.FC<TourCatalogContentProps> = ({
    tourOperator,
    tourPackage,
    tourCatalog,
    goToSlide
}) => {
    const [activeTab, setActiveTab] = useState<TabType>('introduction');
    const [selectedDayIndex, setSelectedDayIndex] = useState<number | null>(0);
    const operatorColor = tourOperator.primaryColor || '#3B82F6';
    const isMice = isMiceCatalog(tourCatalog);
    const nodeRef = useRef(null);
    const navContainerRef = useRef<HTMLDivElement>(null);
    const tabRefs = useRef<Record<TabType, HTMLLIElement | null>>({
        introduction: null,
        itinerary: null,
        includes: null,
        tripbook: null,
        notes: null,
        hotels: null
    });

    // Computed once — parent (ItineraryCatalogView) already tracks resize;
    // reading window.innerWidth on every render is an unnecessary synchronous DOM access.
    const isMobile = useMemo(() => window.innerWidth < 768, []);

    // Memoized style objects — avoids creating new objects on every render
    const mobileNavStyle = useMemo(() => ({
        maxWidth: '100vw',
        WebkitOverflowScrolling: 'touch' as const,
        backdropFilter: isMobile ? 'blur(14.100000381469727px)' : 'none',
    }), [isMobile]);

    const backButtonStyle = useMemo(() => ({
        boxShadow: isMobile ? "0px 4px 4px 0px #00000040" : "none"
    }), [isMobile]);

    // Scroll to selected tab
    useEffect(() => {
        if (navContainerRef.current && tabRefs.current[activeTab]) {
            const container = navContainerRef.current;
            const selectedTab = tabRefs.current[activeTab];
            const containerWidth = container.offsetWidth;
            const tabOffsetLeft = selectedTab.offsetLeft;

            const scrollPosition = tabOffsetLeft - (containerWidth / 4);

            container.scrollTo({
                left: scrollPosition,
                behavior: 'smooth'
            });
        }
    }, [activeTab]);

    const handleTabClick = useCallback((tab: TabType) => {
        setActiveTab(tab);
    }, []);

    const handleBackClick = useCallback(() => {
        goToSlide(1);
        setActiveTab('introduction');
    }, [goToSlide]);

    // Stable ref callbacks — avoids React re-running ref logic on every render
    const setIntroductionRef = useCallback((el: HTMLLIElement | null) => { tabRefs.current.introduction = el; }, []);
    const setItineraryRef = useCallback((el: HTMLLIElement | null) => { tabRefs.current.itinerary = el; }, []);
    const setIncludesRef = useCallback((el: HTMLLIElement | null) => { tabRefs.current.includes = el; }, []);
    const setTripbookRef = useCallback((el: HTMLLIElement | null) => { tabRefs.current.tripbook = el; }, []);
    const setNotesRef = useCallback((el: HTMLLIElement | null) => { tabRefs.current.notes = el; }, []);
    const setHotelsRef = useCallback((el: HTMLLIElement | null) => { tabRefs.current.hotels = el; }, []);

    // Memoized tab content — only reconstructs the active tab's JSX when its
    // relevant data changes, not on every render of the parent.
    const tabContent = useMemo(() => {
        switch (activeTab) {
            case 'introduction':
                return <IntroductionTab tourPackage={tourPackage} tourOperator={tourOperator} tourCatalog={tourCatalog}/>;
            case 'itinerary':
                return (
                    <ItineraryTab
                        tourOperator={tourOperator}
                        tourPackage={tourPackage}
                        selectedDayIndex={selectedDayIndex}
                        onDayClick={setSelectedDayIndex}
                        hideDayTimeline={isMice}
                    />
                );
            case 'includes':
                return <IncludesTab tourPackage={tourPackage} tourOperator={tourOperator}/>;
            case 'tripbook':
                return <TripBookTab tripBookUrl={tourPackage.tripBookUrl}/>;
            case 'notes':
                return (
                    <NotesTab
                        termsAndPricing={tourPackage.termsAndPricing}
                        mainImage={tourPackage.mainImage}
                        tourOperator={tourOperator}
                        tourPackage={tourPackage}
                    />
                );
            case 'hotels':
                return (
                    <HotelsTab tourOperator={tourOperator} tourPackage={tourPackage}/>
                );
            default:
                if (activeTab.startsWith('otherTab-')) {
                    const otherTabTitle = activeTab.replace('otherTab-', '');
                    const otherTab = tourPackage.otherTabs?.find(tab => tab.title === otherTabTitle);
                    if (otherTab) {
                        return <OthersTab tourOperator={tourOperator} tourPackage={tourPackage} otherTab={otherTab}/>;
                    }
                }
                return null;
        }
    }, [activeTab, tourPackage, tourOperator, tourCatalog, selectedDayIndex, isMice]);

    return (
        <div className="flex flex-col w-full h-full relative overflow-x-hidden overflow-hidden">
            {/* Floating Navigation Menu with horizontal scroll */}
            <div className="absolute bottom-0 md:top-5 left-2 md:left-auto md:right-[40px] h-[65px] z-20 rounded-md overflow-hidden">
                <div
                    ref={navContainerRef}
                    className="overflow-x-auto hide-scrollbar py-3"
                    style={mobileNavStyle}
                >
                    <ul className="flex flex-row nowrap gap-1 p-0 h-[45px] rounded-lg">
                        <li
                            ref={setIntroductionRef}
                            className={`flex-shrink-0 flex items-center h-[45px] px-4 py-2 cursor-pointer text-sm rounded-l-lg ${activeTab === 'introduction' ? 'text-white font-bold' : 'bg-white text-[#AFAFAF] hover:bg-gray-100'}`}
                            style={{backgroundColor: activeTab === 'introduction' ? operatorColor : ''}}
                            onClick={() => handleTabClick('introduction')}
                        >
                            Introducción
                        </li>
                        <li
                            ref={setItineraryRef}
                            className={`flex-shrink-0 flex items-center h-[45px] px-4 py-2 cursor-pointer text-sm ${activeTab === 'itinerary' ? 'text-white font-bold' : 'bg-white text-[#AFAFAF] hover:bg-gray-100'}`}
                            style={{backgroundColor: activeTab === 'itinerary' ? operatorColor : ''}}
                            onClick={() => handleTabClick('itinerary')}
                        >
                            {isMice ? 'Situación' : 'Itinerario'}
                        </li>
                        <li
                            ref={setIncludesRef}
                            className={`flex-shrink-0 flex items-center h-[45px] px-4 py-2 cursor-pointer text-sm ${activeTab === 'includes' ? 'text-white font-bold' : 'bg-white text-[#AFAFAF] hover:bg-gray-100'}`}
                            style={{backgroundColor: activeTab === 'includes' ? operatorColor : ''}}
                            onClick={() => handleTabClick('includes')}
                        >
                            {isMice ? 'Servicios' : 'Información'}
                        </li>
                        { tourPackage.tripBookUrl ?
                        <li
                            ref={setTripbookRef}
                            className={`flex-shrink-0 flex items-center h-[45px] px-4 py-2 cursor-pointer text-sm ${activeTab === 'tripbook' ? 'text-white font-bold' : 'bg-white text-[#AFAFAF] hover:bg-gray-100'}`}
                            style={{backgroundColor: activeTab === 'tripbook' ? operatorColor : ''}}
                            onClick={() => handleTabClick('tripbook')}
                        >
                            {isMice ? 'Vídeo' : 'TripBook'}
                        </li> : null }
                        <li
                            ref={setNotesRef}
                            className={`hidden flex-shrink-0 flex items-center h-[45px] px-4 py-2 cursor-pointer text-sm rounded-r-lg ${activeTab === 'notes' ? 'text-white font-bold' : 'bg-white text-[#AFAFAF] hover:bg-gray-100'}`}
                            style={{backgroundColor: activeTab === 'notes' ? operatorColor : ''}}
                            onClick={() => handleTabClick('notes')}
                        >
                            Observaciones
                        </li>
                        {
                            tourPackage.hotels?.length > 0 ? (
                                <li
                                    ref={setHotelsRef}
                                    className={`flex-shrink-0 flex items-center h-[45px] px-4 py-2 cursor-pointer text-sm rounded-r-lg ${activeTab === 'hotels' ? 'text-white font-bold' : 'bg-white text-[#AFAFAF] hover:bg-gray-100'}`}
                                    style={{backgroundColor: activeTab === 'hotels' ? operatorColor : ''}}
                                    onClick={() => handleTabClick('hotels')}
                                >
                                    Hoteles
                                </li>
                            ) : null
                        }
                        { tourPackage.otherTabs && tourPackage.otherTabs.length > 0 && tourPackage.otherTabs.map((otherTab) => (
                            <li
                                id={`other-tab-${otherTab.title}`}
                                key={`other-tab-${otherTab.title}`}
                                ref={el => { tabRefs.current[`otherTab-${otherTab.title}` as TabType] = el; }}
                                className={`flex-shrink-0 flex items-center h-[45px] px-4 py-2 cursor-pointer text-sm  ${activeTab === `otherTab-${otherTab.title}` ? 'text-white font-bold' : 'bg-white text-[#AFAFAF] hover:bg-gray-100'}`}
                                style={{backgroundColor: activeTab === `otherTab-${otherTab.title}` ? operatorColor : ''}}
                                onClick={() => handleTabClick(`otherTab-${otherTab.title}` as TabType)}
                            >
                                {otherTab.title}
                            </li>
                        ))}
                    </ul>
                </div>
            </div>

            {/* Back to index button */}
            <div
                onClick={handleBackClick}
                className="md:ml-[50px] bg-white md:bg-white p-3 rounded-xl pl-4 absolute top-[20px] left-4 z-10 cursor-pointer bg-transparent flex items-center gap-2 text-black hover:underline opacity-85 md:opacity-100"
                style={backButtonStyle}
            >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                    <path fillRule="evenodd"
                          d="M15 8a.5.5 0 0 0-.5-.5H2.707l3.147-3.146a.5.5 0 1 0-.708-.708l-4 4a.5.5 0 0 0 0 .708l4 4a.5.5 0 0 0 .708-.708L2.707 8.5H14.5A.5.5 0 0 0 15 8z"/>
                </svg>
                <span className="text-sm font-bold">Volver a circuitos</span>
            </div>

            {/* Tab content with fade transition */}
            <SwitchTransition mode="out-in">
                <CSSTransition
                    key={activeTab}
                    timeout={300}
                    classNames="fade"
                    nodeRef={nodeRef}
                >
                    <div ref={nodeRef} className="w-full h-full">
                        {tabContent}
                    </div>
                </CSSTransition>
            </SwitchTransition>
        </div>
    );
};
